import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, LogIn, LogOut, XCircle, Lock, Unlock, Clock } from 'lucide-react';
import { userService } from '../../services/api';
import { toast } from 'sonner';

const EVENT_META = {
  login:        { label: 'Login',        icon: LogIn,    color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  logout:       { label: 'Logout',       icon: LogOut,   color: 'bg-slate-100  text-slate-700  border-slate-200'   },
  login_failed: { label: 'Login fallito', icon: XCircle, color: 'bg-amber-100  text-amber-800  border-amber-200'   },
  locked:       { label: 'Account bloccato', icon: Lock, color: 'bg-red-100    text-red-800    border-red-200'     },
  unlocked:     { label: 'Sbloccato', icon: Unlock,     color: 'bg-blue-100   text-blue-800   border-blue-200'    },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'medium' }); }
  catch { return iso; }
};

export default function AccessHistoryDialog({ open, onOpenChange, user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await userService.accessHistory(user.id, 100);
      setEvents(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento storico');
    } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => {
    if (open) load();
    else setEvents([]);
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="access-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            Storico accessi — {user?.first_name} {user?.last_name}
            <span className="text-xs text-slate-400 font-normal font-mono">({user?.email})</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs text-slate-500 border-b pb-2">
          <span>Ultimi 90 giorni · massimo 100 eventi</span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="reload-access-history">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          {loading && events.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-10">Caricamento…</div>
          ) : events.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-10" data-testid="access-history-empty">
              Nessun evento registrato.
            </div>
          ) : (
            <ol className="relative border-l-2 border-slate-100 ml-3 mt-3 space-y-4">
              {events.map((e) => {
                const meta = EVENT_META[e.event_type] || { label: e.event_type, icon: Clock, color: 'bg-slate-100 text-slate-700 border-slate-200' };
                const Icon = meta.icon;
                return (
                  <li key={e.id} className="ml-4" data-testid={`access-event-${e.id}`}>
                    <div className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white ${meta.color.split(' ')[0]}`} />
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`border text-[10px] ${meta.color}`}>
                        <Icon className="w-3 h-3 mr-1 inline" />{meta.label}
                      </Badge>
                      <span className="text-xs font-mono text-slate-500">{fmt(e.created_at)}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                      <span>IP: <span className="font-mono text-slate-700">{e.ip || '—'}</span></span>
                      {e.user_agent && <span className="truncate max-w-lg">UA: <span className="font-mono">{e.user_agent}</span></span>}
                      {e.extra && Object.keys(e.extra).length > 0 && (
                        <span className="text-slate-400">
                          {Object.entries(e.extra).map(([k, v]) => `${k}=${v}`).join(' · ')}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
